// Import react elements
import { useContext, useEffect, useState } from "react";

// Import strich api calls
import { updatePayPalPayoutEmail } from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import components
import { GridItem } from "global/components/Grid";
import CreateButton from "global/components/CreateButton";
import HelpButton from "global/components/HelpButton";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";


const PayPalPayoutEmail = ({ userData }) => {

    // Get the authentication token from the AuthContext
    const { token } = useContext(AuthContext);

    // Get the user refresh function from the user context
    const { refreshUserData } = useContext(UserContext);

    // Store the editable payout email locally
    const [paypalPayoutEmail, setPayPalPayoutEmail] = useState(userData?.paypal_payout_email || "");

    // Track button loading state
    const [updatingPaypalPayoutEmail, setUpdatingPayPalPayoutEmail] = useState(false);

    // Check whether there is currently a saved PayPal payout email
    const hasExistingPaypalPayoutEmail = (userData?.paypal_payout_email || "").trim() !== "";

    // Keep local state in sync if userData changes
    useEffect(() => {

        // Update the local input value when the stored user data changes
        setPayPalPayoutEmail(userData?.paypal_payout_email || "");

    }, [userData]);


    // Handle removing the stored PayPal payout email
    const handleRemovePayPalPayoutEmail = async () => {

        // Get the currently saved PayPal payout email
        const existing_paypal_payout_email = (userData?.paypal_payout_email || "").trim();

        // Check whether there is anything to remove
        if (existing_paypal_payout_email === "") {

            // Tell the user there is nothing stored to remove
            alert("There is no PayPal payout email to remove.");

            // Stop here
            return;

        }

        // Ask the user to confirm removal before sending the update
        DisplayConfirmationDialog(

            "Are you sure you want to remove your PayPal payout email <strong>" + existing_paypal_payout_email + "</strong>?",
            "Remove",
            "Cancel",

            // Handle the user's confirmation response
            async function (is_confirmed) {

                // Stop here if they cancelled
                if (!is_confirmed) return;

                // Set loading state while the request runs
                setUpdatingPayPalPayoutEmail(true);

                try {

                    // Send null to test whether this clears the stored email
                    const response = await updatePayPalPayoutEmail(token, null);

                    // Check whether the update succeeded
                    if (response?.success) {

                        // Clear the local input value
                        setPayPalPayoutEmail("");

                        // Refresh the user data so the page reflects the saved value
                        await refreshUserData();

                    } else {

                        // Show the returned API error if available
                        alert(response?.error || response?.error_message || "Oops...something went wrong.");

                        // Log the full API response for debugging
                        console.error("API error:", response);

                    }

                } catch (error) {

                    // Log the request error for debugging
                    console.error("Remove PayPal payout email error:", error);

                    // Show a generic failure message
                    alert("Oops...something went wrong.");

                } finally {

                    // Clear loading state once the request is finished
                    setUpdatingPayPalPayoutEmail(false);

                }

            }

        );

    };


    // Handle updating the PayPal payout email
    const handleUpdatePayPalPayoutEmail = async () => {

        // Get the currently saved PayPal payout email
        const existingPaypalPayoutEmail = (userData?.paypal_payout_email || "").trim();

        // Get the new email value from the input
        const newPaypalPayoutEmail = (paypalPayoutEmail || "").trim();

        // Check that there is a value to save
        if (newPaypalPayoutEmail === "") {

            // Tell the user they need to enter an email address
            alert("Please enter a PayPal payout email.");

            // Stop here
            return;

        }

        // Check whether anything has changed
        if (newPaypalPayoutEmail === existingPaypalPayoutEmail) {

            // Tell the user the value is unchanged
            alert("That PayPal payout email is already set.");

            // Stop here
            return;

        }

        const messageInfo = "<br/><br/>Please make sure the email address is correct before continuing. All future payout requests will be sent to this PayPal account.<br/><br/><p class='warning-message'>WARNING: Payments sent to an incorrect email address cannot be recalled or reissued.</p> "

        // Build the default confirmation message for when no old value existed
        let confirmation_message = "Are you sure you want to change your PayPal payout email to <strong>" + newPaypalPayoutEmail + "</strong>?" + messageInfo;

        // If there is already an existing value, show both old and new in the confirmation
        if (existingPaypalPayoutEmail !== "") {

            // Update the confirmation message to include the previous email
            confirmation_message = "Are you sure you want to change your PayPal payout email from <strong>" + existingPaypalPayoutEmail + "</strong> to <strong>" + newPaypalPayoutEmail + "</strong>?" + messageInfo;

        }

        // Ask the user to confirm the change before saving
        DisplayConfirmationDialog(

            confirmation_message,
            existingPaypalPayoutEmail !== "" ? "Update" : "Yes",
            "Cancel",

            // Handle the user's confirmation response
            async function (is_confirmed) {

                // Stop here if they cancelled
                if (!is_confirmed) return;

                // Set loading state while the request runs
                setUpdatingPayPalPayoutEmail(true);

                try {

                    // Send the updated PayPal payout email to the API
                    const response = await updatePayPalPayoutEmail(token, newPaypalPayoutEmail);

                    // Check whether the update succeeded
                    if (response?.success) {

                        // Refresh the user data so the page reflects the saved value
                        await refreshUserData();

                    } else {

                        // Show the returned API error if available
                        alert(response?.error || response?.error_message || "Oops...something went wrong.");

                        // Log the full API response for debugging
                        console.error("API error:", response);

                    }

                } catch (error) {

                    // Log the request error for debugging
                    console.error("Update PayPal payout email error:", error);

                    // Show a generic failure message
                    alert("Oops...something went wrong.");

                } finally {

                    // Clear loading state once the request is finished
                    setUpdatingPayPalPayoutEmail(false);

                }

            }

        );

    };



    return (

        <GridItem>

            <h3>PayPal Payout Email <HelpButton section="account" code="payout-email" label="PayPal Payout Email" /></h3>
            
            <br />

            <p>This is the account that we will pay money into when you request an earnings payout.</p>
            
            <br />
           
            <input
                type="email"
                name="paypal_payout_email"
                value={paypalPayoutEmail}
                onChange={(e) => setPayPalPayoutEmail(e.target.value)}
                placeholder="Enter PayPal Email..."
            />

            <br />
            <br />

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>

                <CreateButton
                    title={updatingPaypalPayoutEmail ? (hasExistingPaypalPayoutEmail ? "Updating..." : "Adding...") : (hasExistingPaypalPayoutEmail ? "Update PayPal Email" : "Add PayPal Email")}
                    size="s"
                    onClick={handleUpdatePayPalPayoutEmail}
                    disabled={updatingPaypalPayoutEmail || !paypalPayoutEmail || (paypalPayoutEmail === userData?.paypal_payout_email)}
                />

                {hasExistingPaypalPayoutEmail ?

                    <CreateButton
                        title={updatingPaypalPayoutEmail ? "Removing..." : "Remove PayPal Email"}
                        size="s"
                        invert={true}
                        onClick={handleRemovePayPalPayoutEmail}
                        disabled={updatingPaypalPayoutEmail}
                    />

                    :

                    null

                }

            </div>

        </GridItem>

    );

};

export default PayPalPayoutEmail;